import {useEffect, useState, useSyncExternalStore} from "react";
import {useLocation} from "react-router-dom";
import {Box, Button, IconButton, ListItemIcon, Menu, MenuItem, Stack} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CampaignIcon from "@mui/icons-material/Campaign";
import {printOrder} from "../../utils/ordersControlar";
import {restartApplication} from "../../utils/restartApplication";
import {isNativeApp, postToNative} from "../../utils/nativeBridge";
import PickupBoardDialog from "./PickupBoardDialog";

const POS_ACCOUNTS = ["POS1", "POS2", "POS3"];
const OPEN_LABEL = "開啟 POS 快速功能";

// 內嵌 ⋮（例如訂單摘要標題）掛載數；大於 0 時右下浮動鈕讓位，避免蓋住送單/結帳
let inlineCount = 0;
const listeners = new Set();
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const getInlineCount = () => inlineCount;
const setInlineCount = (next) => { inlineCount = next; listeners.forEach((fn) => fn()); };

function usePosAccount() {
    const location = useLocation();
    const [account, setAccount] = useState(() => localStorage.getItem("account") || "");

    useEffect(() => {
        setAccount(localStorage.getItem("account") || "");
    }, [location.pathname]);

    useEffect(() => {
        const syncAccount = () => setAccount(localStorage.getItem("account") || "");
        window.addEventListener("storage", syncAccount);
        window.addEventListener("focus", syncAccount);
        return () => {
            window.removeEventListener("storage", syncAccount);
            window.removeEventListener("focus", syncAccount);
        };
    }, []);

    return account.toUpperCase();
}

/** 快速功能清單；POS 以外的帳號回傳 null。openPickup：開啟取餐叫號面板 */
function usePosQuickActions(close, openPickup) {
    const posAccount = usePosAccount();
    if (!POS_ACCOUNTS.includes(posAccount)) return null;

    const handleOpenCashDrawer = () => {
        close();
        printOrder({orderId: 0, type: 4});
    };

    const handlePickup = () => {
        close();
        openPickup();
    };

    const handleLogout = () => {
        close();
        if (window.confirm("確定是否登出?")) {
            localStorage.removeItem('token');
            localStorage.removeItem('_ac');
            postToNative('logout');
            window.location.href = '/'; // App 內也回登入頁，原生收到 logout 會接手
        }
    };

    const handleRefresh = () => {
        close();
        window.location.reload();
    };

    const handleRestart = () => {
        close();
        if (window.confirm("確定要清除應用程式快取，並重新載入最新版本嗎？")) {
            restartApplication();
        }
    };

    // 開啟錢櫃只有結帳台(pos1)有錢櫃硬體，內場機台(pos2/pos3)不顯示
    return [
        ...(posAccount === "POS1" ? [{label: "開啟錢櫃", icon: <PointOfSaleIcon/>, onClick: handleOpenCashDrawer}] : []),
        {label: "取餐叫號", icon: <CampaignIcon/>, onClick: handlePickup},
        {label: "登出", icon: <LogoutIcon/>, onClick: handleLogout},
        ...(isNativeApp() ? [] : [
            {label: "重新載入頁面", icon: <RefreshIcon/>, onClick: handleRefresh},
            {label: "清除快取並重啟", icon: <RestartAltIcon/>, onClick: handleRestart},
        ]),
    ];
}

/** 右下浮動 ⋮（全站）；頁面有內嵌 ⋮ 時自動隱藏 */
export function PosQuickActionsFab() {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [pickupOpen, setPickupOpen] = useState(false);
    const inline = useSyncExternalStore(subscribe, getInlineCount);
    const actions = usePosQuickActions(() => setOpen(false), () => setPickupOpen(true));

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    if (!actions || inline > 0) return null;
    // 面板開著時收起浮動鈕（它疊在 modal 之上，會蓋住號碼）
    if (pickupOpen) return <PickupBoardDialog open onClose={() => setPickupOpen(false)}/>;

    return (
        <Box
            data-testid="pos-quick-actions-fab"
            sx={{
                position: "fixed",
                right: {xs: 10, sm: 14},
                bottom: {xs: "calc(env(safe-area-inset-bottom, 0px) + 10px)", sm: 14},
                zIndex: (theme) => theme.zIndex.modal + 1,
            }}
        >
            {open && (
                <Stack
                    spacing={1}
                    alignItems="flex-end"
                    sx={{
                        position: "absolute",
                        right: 0,
                        bottom: 64,
                    }}
                >
                    {actions.map((action) => (
                        <Button
                            key={action.label}
                            variant="contained"
                            startIcon={action.icon}
                            onClick={action.onClick}
                            sx={{
                                minWidth: 188,
                                minHeight: 52,
                                borderRadius: "999px",
                                px: 2.25,
                                justifyContent: "flex-start",
                                fontSize: 16,
                                fontWeight: 900,
                                whiteSpace: "nowrap",
                                backgroundColor: "#fff",
                                color: "#1f2937",
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 8px 20px rgba(20,30,40,0.18)",
                                "&:hover": {
                                    backgroundColor: "#EDF0F4",
                                    borderColor: "#1C2D42",
                                },
                                "& .MuiButton-startIcon": {
                                    color: "#1C2D42",
                                    marginRight: 1.25,
                                    "& svg": {
                                        fontSize: 26,
                                    },
                                },
                            }}
                        >
                            {action.label}
                        </Button>
                    ))}
                </Stack>
            )}
            <IconButton
                onClick={() => setOpen((prev) => !prev)}
                aria-label={open ? "關閉 POS 快速功能" : OPEN_LABEL}
                sx={{
                    width: 52,
                    height: 52,
                    color: "#fff",
                    backgroundColor: "#1C2D42",
                    boxShadow: "0 8px 18px rgba(28,45,66,0.32)",
                    "&:hover": {
                        backgroundColor: "#0C1625",
                    },
                }}
            >
                {open ? <CloseIcon/> : <MoreVertIcon/>}
            </IconButton>
        </Box>
    );
}

/** 內嵌的 ⋮（訂單摘要深色標題列、出餐狀況分頁頁碼列），掛載期間取代右下浮動鈕；tone="light" 用在白底 */
export function PosQuickActionsMenuButton({tone = "dark"}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [pickupOpen, setPickupOpen] = useState(false);
    const actions = usePosQuickActions(() => setAnchorEl(null), () => setPickupOpen(true));
    const enabled = Boolean(actions);

    useEffect(() => {
        if (!enabled) return undefined;
        setInlineCount(inlineCount + 1);
        return () => setInlineCount(inlineCount - 1);
    }, [enabled]);

    if (!actions) return null;

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label={OPEN_LABEL}
                aria-haspopup="menu"
                sx={tone === "light" ? {
                    width: 52,
                    height: 52,
                    color: "#FFF",
                    backgroundColor: "#1C2D42",
                    "&:hover": {backgroundColor: "#0C1625"},
                } : {
                    width: 36,
                    height: 36,
                    color: "#FFF",
                    border: "1px solid rgba(255,255,255,0.35)",
                    "&:hover": {backgroundColor: "rgba(255,255,255,0.12)"},
                }}
            >
                <MoreVertIcon sx={{fontSize: tone === "light" ? 26 : 22}}/>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{vertical: "top", horizontal: "right"}}
                transformOrigin={{vertical: "bottom", horizontal: "right"}}
                slotProps={{paper: {sx: {borderRadius: "16px", minWidth: 188}}}}
            >
                {actions.map((action) => (
                    <MenuItem
                        key={action.label}
                        onClick={action.onClick}
                        sx={{minHeight: 52, fontSize: 16, fontWeight: 900, color: "#1f2937"}}
                    >
                        <ListItemIcon sx={{color: "#1C2D42", "& svg": {fontSize: 26}}}>{action.icon}</ListItemIcon>
                        {action.label}
                    </MenuItem>
                ))}
            </Menu>
            <PickupBoardDialog open={pickupOpen} onClose={() => setPickupOpen(false)}/>
        </>
    );
}
